import React, { useState, useEffect } from 'react';
import Footer from './common/Footer';
import Sidebar from './Sidebar';
import { DataTable } from './common/StatCard';
import studentService from '../services/studentService';

const DashboardScreen = ({ onNavigate, onLogout }) => {
  const [stats, setStats] = useState({ totalStudents: 0, pendingRegistrations: 0, approvedRegistrations: 0, rejectedRegistrations: 0 });
  const [students, setStudents] = useState([]);
  const [archivedStudents, setArchivedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const statsData = await studentService.getDashboardStats();
      setStats(statsData);
      try {
        const studentsData = await studentService.getAllStudents();
        setStudents(studentsData.map(s => ({
          id: s.registrationId,
          name: s.fullName,
          date: new Date(s.createdAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }),
            gender: s.gender || 'N/A',   // ✅ ADD THIS

          hometown: s.nativeRegion || 'N/A',
          contact: s.mobile,
          status: s.status.toLowerCase(),
          source: 'Portal',
        })));
      } catch (err) {
        console.log('Could not fetch student list (may not have admin permissions)');
      }
      try {
        // Archive = REJECTED + APPROVED (confirmed) students
        const [rejectedData, approvedData] = await Promise.all([
          studentService.getStudentsByStatus('REJECTED').catch(() => []),
          studentService.getStudentsByStatus('APPROVED').catch(() => []),
        ]);
        const archived = [
          ...(rejectedData || []).map(s => ({
            id: s.registrationId,
            name: s.fullName,
            date: new Date(s.createdAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }),
            gender: s.gender || 'N/A',
            hometown: s.nativeRegion || 'N/A',
            contact: s.mobile,
            status: 'rejected',
            source: 'Portal',
          })),
          ...(approvedData || []).map(s => ({
            id: s.registrationId,
            name: s.fullName,
            date: new Date(s.createdAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }),
            gender: s.gender || 'N/A',
            hometown: s.nativeRegion || 'N/A',
            contact: s.mobile,
            status: 'confirmed',   // show "confirmed" not "approved"
            source: 'Portal',
          })),
        ];
        setArchivedStudents(archived);
      } catch (err) {
        console.log('Could not fetch archived students');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally { setLoading(false); }
  };

  const handleViewRegistration = (registrationId) => {
    onNavigate('registrationView', { registrationId });
  };

  // "All" tab = active students only (exclude rejected & confirmed/approved — those go to Archive)
  const filteredStudents = students.filter(s =>
    s.status !== 'rejected' && s.status !== 'confirmed' && s.status !== 'approved' &&
    (!search || s.name.toLowerCase().includes(search.toLowerCase()) || s.id.toLowerCase().includes(search.toLowerCase()))
  );
  const filteredArchived = archivedStudents.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.id.toLowerCase().includes(search.toLowerCase())
  );

  const tabStyle = (tab) => ({
    padding: '14px 0',
    marginRight: 40,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: activeTab === tab ? '#1B3B5F' : '#9B9B9B',
    borderBottom: activeTab === tab ? '3px solid #D4AF37' : '3px solid transparent',
    transition: 'all 0.2s',
    fontFamily: 'Work Sans,sans-serif',
  });

  return (
    <div className="app-layout">
      <Sidebar activeItem="dashboard" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content">
        <div className="top-bar">
          <h1 className="page-title">Registrations </h1>
          <div className="top-actions">
            <button className=" addreg-btn" onClick={() => onNavigate('registration1')}>+ Register New</button>
          </div>
        </div>
        <div className="content-area">
          {error && (
            <div style={{ padding: 15, marginBottom: 20, backgroundColor: '#fee', border: '1px solid #e74c3c', borderRadius: 8, color: '#e74c3c', fontSize: 14, fontWeight: 500 }}>{error}</div>
          )}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, fontSize: 16, color: '#666' }}>Loading...</div>
          ) : (
            <div className="data-table-container">
              {/* Header row */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'24px 28px 0' }}>
                <h2 className="table-title" style={{ margin:0 }}>Student Registrations</h2>
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 18px', background:'#F4F2EF', borderRadius:30 }}>
                  <span style={{ fontSize:15, color:'#aaa' }}>🔍</span>
                  <input
                    style={{ border:'none', background:'transparent', outline:'none', fontSize:14, width:200, color:'#2C2C2C', fontFamily:'Work Sans,sans-serif' }}
                    placeholder="Search students..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display:'flex', borderBottom:'1px solid #E8E4DF', padding:'0 28px', marginTop:8 }}>
                <button style={tabStyle('all')} onClick={() => setActiveTab('all')}>All Registrations</button>
                <button style={tabStyle('archived')} onClick={() => setActiveTab('archived')}>Archived</button>
              </div>

              {/* Table */}
              {activeTab === 'all' ? (
                filteredStudents.length > 0 ? (
                  <DataTable data={filteredStudents} onViewRegistration={handleViewRegistration} />
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    {search ? 'No matching registrations.' : 'No registrations yet. Click \"Register New\" to add a student.'}
                  </div>
                )
              ) : (
                filteredArchived.length > 0 ? (
                  <DataTable data={filteredArchived} onViewRegistration={handleViewRegistration} />
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    {search ? 'No matching archived records.' : 'No archived (rejected / confirmed) registrations.'}
                  </div>
                )
              )}
            </div>
          )}
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default DashboardScreen;